import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { platformSpendHistory, topPlatformsWithOther, type PlatformReport } from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { Kpi } from "./ui";
import { money, monthLabel } from "../lib/format";
import { percentChange } from "../lib/platformBilling";
import { ui } from "../theme";
import { PLATFORMS_NS } from "../locales/platformsI18n";

// Gráficas del encabezado de Pago de plataformas: lo pagado en el año hasta hoy,
// la tendencia mes a mes y por año, y el comparativo contra los años anteriores.
// Todo en moneda base (MXN), a partir del reporte mensual por plataforma.

/** Color por antigüedad del año: 0 = año en curso. Paleta validada en claro y oscuro. */
const YEAR_COLORS = ["#0d9488", "#6366f1", "#d97706", "#db2777"];
const CURRENT = YEAR_COLORS[0];
const MONTHS_ES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const MONTHS_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const kFormat = (v: unknown) => {
  const n = Number(v);
  return Math.abs(n) >= 1000 ? `$${Math.round(n / 1000)}k` : `$${Math.round(n)}`;
};

function Toggle<T extends string>({ value, options, onChange }: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void }) {
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  return (
    <div className={`inline-flex rounded-lg border p-0.5 text-xs ${c.border}`} role="group">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={`rounded-md px-2 py-1 ${value === o.value ? "bg-teal-600 text-white" : c.muted}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function PlatformSpendCharts({ report, today }: { report: PlatformReport[] | null; today: string }) {
  const { t: tp, i18n } = useTranslation(PLATFORMS_NS);
  const language = i18n.language;
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const [trend, setTrend] = useState<"mensual" | "anual">("mensual");
  const [compare, setCompare] = useState<"mes" | "acumulado">("mes");

  const h = useMemo(() => (report ? platformSpendHistory(report, today) : null), [report, today]);
  const axis = { fontSize: 11, fill: isDarkMode ? "#9ca3af" : "#6b7280" };
  const grid = isDarkMode ? "#333" : "#e5e7eb";
  const tooltipStyle = { fontSize: 12, backgroundColor: isDarkMode ? "#1f1f1f" : "#fff", borderColor: grid, color: isDarkMode ? "#fff" : "#111" };
  const fmt = (v: unknown) => money(Number(v), "MXN", language);

  const byPlatform = useMemo(() => {
    if (!h) return [];
    const { rows, otherCount } = topPlatformsWithOther(h.byPlatformYtd, 8);
    return rows.map((r) => ({ ...r, name: r.platformId === "__other__" ? tp("charts.others", { count: otherCount }) : r.name }));
  }, [h, tp]);

  // Últimos 4 años como mucho, del más nuevo al más viejo (el color sigue al año).
  const shownYears = useMemo(() => (h ? h.years.slice(-YEAR_COLORS.length).reverse() : []), [h]);
  const compareData = useMemo(() => {
    if (!h) return [];
    const names = language.startsWith("en") ? MONTHS_EN : MONTHS_ES;
    const running: Record<number, number> = {};
    return names.map((label, i) => {
      const row: Record<string, string | number | null> = { label };
      for (const y of shownYears) {
        // El año en curso se corta en el mes actual: los meses por venir no son cero.
        if (y.year === h.year && i + 1 > h.month) {
          row[String(y.year)] = null;
          continue;
        }
        running[y.year] = (running[y.year] || 0) + y.months[i];
        row[String(y.year)] = compare === "acumulado" ? Math.round(running[y.year] * 100) / 100 : y.months[i];
      }
      return row;
    });
  }, [h, shownYears, compare, language]);

  if (!h) return null;

  const trendData =
    trend === "mensual"
      ? h.monthly.map((m) => ({ label: monthLabel(m.period, language), total: m.total, current: m.period.startsWith(`${h.year}-`) }))
      : h.years.map((y) => ({ label: y.year === h.year ? tp("charts.yearToDate", { year: y.year }) : String(y.year), total: y.total, current: y.year === h.year }));
  const hasData = h.allTime > 0;
  const firstYear = h.years[0]?.year;

  return (
    <section aria-label={tp("charts.title")} className="space-y-3" data-testid="platform-spend-charts">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi label={tp("charts.kpi.ytd", { year: h.year })} value={money(h.ytd, "MXN", language)} hint={tp("charts.kpi.ytdHint", { month: monthLabel(today.slice(0, 7), language) })} />
        <Kpi
          label={tp("charts.kpi.vsPrevious", { year: h.year - 1 })}
          value={percentChange(h.ytdChange, language)}
          hint={h.previousToDate != null ? tp("charts.kpi.previousHint", { value: money(h.previousToDate, "MXN", language) }) : tp("charts.kpi.noPrevious")}
          tone={h.ytdChange == null ? undefined : h.ytdChange > 0 ? "bad" : "good"}
        />
        <Kpi
          label={tp("charts.kpi.allTime", { year: firstYear })}
          value={money(h.allTime, "MXN", language)}
          hint={h.chargesWithoutRate ? tp("charts.kpi.withoutRate", { count: h.chargesWithoutRate }) : undefined}
        />
      </div>

      {!hasData ? (
        <div className={`${c.card} text-sm ${c.muted}`}>{tp("charts.empty")}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div className={c.card}>
              <h2 className={`text-sm font-semibold ${c.text}`}>{tp("charts.paid.title", { year: h.year })}</h2>
              <p className={`mb-2 text-xs ${c.muted}`}>{tp("charts.paid.subtitle")}</p>
              {byPlatform.length === 0 ? (
                <p className={`py-10 text-center text-sm ${c.muted}`}>{tp("charts.paid.empty", { year: h.year })}</p>
              ) : (
                <div style={{ height: Math.max(160, byPlatform.length * 30 + 30) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={byPlatform} layout="vertical" margin={{ left: 8, right: 16 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={grid} horizontal={false} />
                      <XAxis type="number" tick={axis} tickFormatter={kFormat} />
                      <YAxis type="category" dataKey="name" tick={axis} width={150} interval={0} />
                      <Tooltip formatter={(v) => [fmt(v), tp("charts.paid.series")]} contentStyle={tooltipStyle} cursor={{ fill: isDarkMode ? "#ffffff10" : "#0000000a" }} />
                      <Bar dataKey="paid" fill={CURRENT} radius={[0, 4, 4, 0]} maxBarSize={18} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className={c.card}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h2 className={`text-sm font-semibold ${c.text}`}>{tp("charts.trend.title")}</h2>
                  <p className={`mb-2 text-xs ${c.muted}`}>{tp(trend === "mensual" ? "charts.trend.monthlyHint" : "charts.trend.yearlyHint")}</p>
                </div>
                <Toggle
                  value={trend}
                  onChange={setTrend}
                  options={[
                    { value: "mensual", label: tp("charts.trend.monthly") },
                    { value: "anual", label: tp("charts.trend.yearly") },
                  ]}
                />
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
                    <XAxis dataKey="label" tick={axis} interval="preserveStartEnd" minTickGap={8} />
                    <YAxis tick={axis} tickFormatter={kFormat} width={52} />
                    <Tooltip formatter={(v) => [fmt(v), tp("charts.paid.series")]} contentStyle={tooltipStyle} cursor={{ fill: isDarkMode ? "#ffffff10" : "#0000000a" }} />
                    <Bar dataKey="total" radius={[4, 4, 0, 0]} maxBarSize={trend === "anual" ? 56 : 22}>
                      {trendData.map((d) => (
                        <Cell key={d.label} fill={d.current ? YEAR_COLORS[0] : YEAR_COLORS[1]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className={`mt-1 flex items-center gap-3 text-xs ${c.muted}`}>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: YEAR_COLORS[0] }} />
                  {h.year}
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: YEAR_COLORS[1] }} />
                  {tp("charts.trend.previousYears")}
                </span>
              </p>
            </div>
          </div>

          <div className={c.card}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h2 className={`text-sm font-semibold ${c.text}`}>{tp("charts.compare.title")}</h2>
                <p className={`mb-2 text-xs ${c.muted}`}>{tp(compare === "mes" ? "charts.compare.monthlyHint" : "charts.compare.cumulativeHint")}</p>
              </div>
              <Toggle
                value={compare}
                onChange={setCompare}
                options={[
                  { value: "mes", label: tp("charts.compare.byMonth") },
                  { value: "acumulado", label: tp("charts.compare.cumulative") },
                ]}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="h-64 lg:col-span-2">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={compareData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
                    <XAxis dataKey="label" tick={axis} />
                    <YAxis tick={axis} tickFormatter={kFormat} width={52} />
                    <Tooltip formatter={(v, name) => [fmt(v), String(name)]} contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {shownYears.map((y, i) => (
                      <Line
                        key={y.year}
                        type="monotone"
                        dataKey={String(y.year)}
                        name={String(y.year)}
                        stroke={YEAR_COLORS[i]}
                        strokeWidth={2}
                        strokeDasharray={i === 0 ? undefined : "5 3"}
                        dot={{ r: 3, strokeWidth: 2, fill: isDarkMode ? "#1a1a19" : "#fff" }}
                        activeDot={{ r: 5 }}
                        connectNulls={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <table className="w-full self-start text-sm" aria-label={tp("charts.compare.table")}>
                <thead>
                  <tr className={`text-left text-xs ${c.muted}`}>
                    <th className="py-1">{tp("charts.compare.year")}</th>
                    <th className="py-1 text-right">{tp("charts.compare.toDate", { month: monthLabel(today.slice(0, 7), language).split(" ")[0] })}</th>
                    <th className="py-1 text-right">{tp("charts.compare.total")}</th>
                    <th className="py-1 text-right">{tp("charts.compare.change")}</th>
                  </tr>
                </thead>
                <tbody>
                  {shownYears.map((y, i) => {
                    const prev = h.years.find((x) => x.year === y.year - 1);
                    const change = prev && prev.toDate > 0 ? (y.toDate - prev.toDate) / prev.toDate : null;
                    return (
                      <tr key={y.year} className={`border-t ${c.border} ${c.text}`} data-testid={`year-${y.year}`}>
                        <td className="py-1.5">
                          <span className="mr-1.5 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: YEAR_COLORS[i] }} />
                          {y.year}
                        </td>
                        <td className="whitespace-nowrap py-1.5 text-right tabular-nums">{money(y.toDate, "MXN", language)}</td>
                        <td className="whitespace-nowrap py-1.5 text-right tabular-nums">
                          {money(y.total, "MXN", language)}
                          {y.year === h.year && <span className={`ml-1 text-xs ${c.muted}`}>*</span>}
                        </td>
                        <td className={`py-1.5 text-right tabular-nums ${c.muted}`}>{percentChange(change, language)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className={`mt-2 text-xs ${c.muted}`}>{tp("charts.compare.footnote")}</p>
          </div>
        </>
      )}
    </section>
  );
}
